import React, { useState, Fragment } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Settings, BookOpen, FileText } from 'lucide-react';
import { useCmsBlocks, CmsBlock, CmsBlockInsert } from '@/hooks/useCmsBlocks';
import { useInstructions, Instruction } from '@/hooks/useInstructions';
import { useToast } from '@/hooks/use-toast';
import InstructionModal from './InstructionModal';

const COLOR_OPTIONS = [
  { value: 'bg-blue-500', label: 'Niebieski', color: 'bg-blue-500' },
  { value: 'bg-green-500', label: 'Zielony', color: 'bg-green-500' },
  { value: 'bg-purple-500', label: 'Fioletowy', color: 'bg-purple-500' },
  { value: 'bg-orange-500', label: 'Pomarańczowy', color: 'bg-orange-500' },
  { value: 'bg-red-500', label: 'Czerwony', color: 'bg-red-500' },
  { value: 'bg-emerald-500', label: 'Szmaragdowy', color: 'bg-emerald-500' },
  { value: 'bg-indigo-500', label: 'Indygo', color: 'bg-indigo-500' },
  { value: 'bg-pink-500', label: 'Różowy', color: 'bg-pink-500' },
];

const EMOJI_OPTIONS = [
  '🔍', '📈', '📊', '⚡', '🎯', '🚀', '💡', '📝', '🔧', '📱',
  '🌐', '📋', '🎨', '📌', '⭐', '🔥', '💎', '🎪', '🎭', '🎵'
];

interface CmsAdminPanelProps {
  className?: string;
}

export function CmsAdminPanel({ className }: CmsAdminPanelProps) {
  const { blocks, loading, error, createBlock, updateBlock, deleteBlock, isAdmin } = useCmsBlocks();
  const { instructions, createInstruction, updateInstruction, deleteInstruction, getInstructionById } = useInstructions();
  const { toast } = useToast();
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(false);
  const [isEditInstructionDialogOpen, setIsEditInstructionDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<CmsBlock | null>(null);
  const [selectedInstruction, setSelectedInstruction] = useState<Instruction | null>(null);
  const [editingInstruction, setEditingInstruction] = useState<Instruction | null>(null);
  const [formData, setFormData] = useState<Partial<CmsBlockInsert>>({
    title: '',
    description: '',
    content: '',
    target_url: '',
    emoji: '🔍',
    color: 'bg-blue-500',
    status: 'active',
    sort_order: 0,
    instruction_id: null,
  });
  const [hasInstruction, setHasInstruction] = useState(false);
  const [instructionContent, setInstructionContent] = useState('');

  // Reset form data
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      target_url: '',
      emoji: '🔍',
      color: 'bg-blue-500',
      status: 'active',
      sort_order: 0,
      instruction_id: null,
    });
    setHasInstruction(false);
    setInstructionContent('');
  };


  // Handle create block
  const handleCreateBlock = async () => {
    try {
      if (!formData.title || !formData.description) {
        toast({
          title: "Błąd",
          description: "Tytuł i opis są wymagane",
          variant: "destructive",
        });
        return;
      }

      let instructionId = null;
      
      // Jeśli instrukcja jest zaznaczona, utwórz ją
      if (hasInstruction && instructionContent.trim()) {
        const instruction = await createInstruction({
          title: `Instrukcja dla ${formData.title}`,
          content: instructionContent,
          is_active: true,
        });
        instructionId = instruction.id;
      }

      // Utwórz blok z instrukcją
      await createBlock({
        ...formData,
        instruction_id: instructionId,
      } as CmsBlockInsert);
      
      toast({
        title: "Sukces",
        description: "Blok został utworzony pomyślnie",
      });
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Wystąpił błąd podczas tworzenia bloku",
        variant: "destructive",
      });
    }
  };


  // Handle show instruction
  const handleShowInstruction = (instructionId: string) => {
    const instruction = getInstructionById(instructionId);
    if (instruction) {
      setSelectedInstruction(instruction);
      setIsInstructionModalOpen(true);
    }
  };

  // Handle edit instruction
  const handleEditInstruction = (instructionId: string) => {
    const instruction = getInstructionById(instructionId);
    if (instruction) {
      setEditingInstruction(instruction);
      setIsEditInstructionDialogOpen(true);
    }
  };

  // Handle update instruction
  const handleUpdateInstruction = async () => {
    if (!editingInstruction) return;

    try {
      await updateInstruction(editingInstruction.id, {
        title: editingInstruction.title,
        content: editingInstruction.content,
        is_active: editingInstruction.is_active,
      });

      toast({
        title: "Sukces",
        description: "Instrukcja została zaktualizowana.",
      });

      setIsEditInstructionDialogOpen(false);
      setEditingInstruction(null);
    } catch (error) {
      console.error('Error updating instruction:', error);
      toast({
        title: "Błąd",
        description: "Nie udało się zaktualizować instrukcji.",
        variant: "destructive",
      });
    }
  };

  // Handle edit block
  const handleEditBlock = (block: CmsBlock) => {
    setEditingBlock(block);
    setFormData({
      title: block.title,
      description: block.description,
      content: block.content || '',
      target_url: block.target_url || '',
      emoji: block.emoji || '🔍',
      color: block.color,
      status: block.status,
      sort_order: block.sort_order,
    });
    setIsEditDialogOpen(true);
  };

  // Handle update block
  const handleUpdateBlock = async () => {
    try {
      if (!editingBlock || !formData.title || !formData.description) {
        toast({
          title: "Błąd",
          description: "Tytuł i opis są wymagane",
          variant: "destructive",
        });
        return;
      }

      await updateBlock(editingBlock.id, formData);
      toast({
        title: "Sukces",
        description: "Blok został zaktualizowany pomyślnie",
      });
      setIsEditDialogOpen(false);
      setEditingBlock(null);
      resetForm();
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Wystąpił błąd podczas aktualizacji bloku",
        variant: "destructive",
      });
    }
  };

  // Handle delete block
  const handleDeleteBlock = async (blockId: string) => {
    try {
      await deleteBlock(blockId);
      toast({
        title: "Sukces",
        description: "Blok został usunięty pomyślnie",
      });
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : "Wystąpił błąd podczas usuwania bloku",
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Panel Administratora CMS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Ładowanie...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Panel Administratora CMS
        </CardTitle>
        <CardDescription>
          Zarządzaj dynamicznymi blokami wyświetlanymi na dashboardzie
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create New Block Button */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full" onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Utwórz nowy blok
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Utwórz nowy blok CMS</DialogTitle>
              <DialogDescription>
                Wypełnij poniższe pola, aby utworzyć nowy blok na dashboardzie
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Tytuł *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Nazwa bloku"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emoji">Emotikona</Label>
                  <Select
                    value={formData.emoji}
                    onValueChange={(value) => setFormData({ ...formData, emoji: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz emotikonę" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMOJI_OPTIONS.map((emoji) => (
                        <SelectItem key={emoji} value={emoji}>
                          {emoji}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Opis *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Krótki opis funkcjonalności bloku"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Treść (opcjonalna)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Dodatkowa treść bloku"
                  rows={3}
                />
              </div>

              {/* Instrukcja */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="hasInstruction"
                    checked={hasInstruction}
                    onChange={(e) => setHasInstruction(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="hasInstruction" className="text-sm font-medium">
                    Dodaj instrukcję do tego bloku
                  </Label>
                </div>
                
                {hasInstruction && (
                  <div className="space-y-2">
                    <Label htmlFor="instructionContent">Treść instrukcji (Markdown)</Label>
                    <Textarea
                      id="instructionContent"
                      value={instructionContent}
                      onChange={(e) => setInstructionContent(e.target.value)}
                      placeholder="Wpisz instrukcję w formacie Markdown..."
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Możesz używać składni Markdown: **pogrubienie**, *kursywa*, # nagłówki, - listy, `kod`, itp.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target_url">URL docelowy</Label>
                  <Input
                    id="target_url"
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    placeholder="/path lub https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Kolor</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({ ...formData, color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz kolor" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.color}`}></div>
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value as 'active' | 'inactive' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktywny</SelectItem>
                      <SelectItem value="inactive">Nieaktywny</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sort_order">Kolejność</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handleCreateBlock}>
                Utwórz blok
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Error Display */}
        {error && (
          <div className="text-red-500 text-sm bg-red-50 p-3 rounded">
            {error}
          </div>
        )}

        {/* Blocks List */}
        <div className="space-y-3">
          <h3 className="font-semibold">Zarządzaj blokami ({blocks.length})</h3>
          {blocks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Brak bloków do wyświetlenia
            </div>
          ) : (
            <div className="space-y-2">
              {blocks.map((block) => (
                <div
                  key={block.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded ${block.color} text-white`}>
                      {block.emoji}
                    </div>
                    <div>
                      <div className="font-medium">{block.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {block.description}
                      </div>
                      {block.target_url && (
                        <div className="text-xs text-blue-600">
                          → {block.target_url}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={block.status === 'active' ? 'default' : 'secondary'}>
                      {block.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                    </Badge>
                    {block.instruction_id && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShowInstruction(block.instruction_id!)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Zobacz instrukcję"
                        >
                          <BookOpen className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditInstruction(block.instruction_id!)}
                          className="text-green-600 hover:text-green-700"
                          title="Edytuj instrukcję"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditBlock(block)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Czy na pewno chcesz usunąć ten blok?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Ta akcja nie może zostać cofnięta. Blok "{block.title}" zostanie trwale usunięty.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Anuluj</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteBlock(block.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Usuń
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edytuj blok CMS</DialogTitle>
              <DialogDescription>
                Zmodyfikuj ustawienia wybranego bloku
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Tytuł *</Label>
                  <Input
                    id="edit-title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Nazwa bloku"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-emoji">Emotikona</Label>
                  <Select
                    value={formData.emoji}
                    onValueChange={(value) => setFormData({ ...formData, emoji: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz emotikonę" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMOJI_OPTIONS.map((emoji) => (
                        <SelectItem key={emoji} value={emoji}>
                          {emoji}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-description">Opis *</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Krótki opis funkcjonalności bloku"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-content">Treść (opcjonalna)</Label>
                <Textarea
                  id="edit-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Dodatkowa treść bloku"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-target_url">URL docelowy</Label>
                  <Input
                    id="edit-target_url"
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    placeholder="/path lub https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-color">Kolor</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData({ ...formData, color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz kolor" />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.color}`}></div>
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value as 'active' | 'inactive' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktywny</SelectItem>
                      <SelectItem value="inactive">Nieaktywny</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sort_order">Kolejność</Label>
                  <Input
                    id="edit-sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handleUpdateBlock}>
                Zapisz zmiany
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog do edycji instrukcji */}
        <Dialog open={isEditInstructionDialogOpen} onOpenChange={setIsEditInstructionDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-green-500" />
                Edytuj Instrukcję
              </DialogTitle>
              <DialogDescription>
                Edytuj treść instrukcji w formacie Markdown
              </DialogDescription>
            </DialogHeader>

            {editingInstruction && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-instruction-title">Tytuł instrukcji</Label>
                  <Input
                    id="edit-instruction-title"
                    value={editingInstruction.title}
                    onChange={(e) => setEditingInstruction({
                      ...editingInstruction,
                      title: e.target.value
                    })}
                    placeholder="Wpisz tytuł instrukcji..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-instruction-content">Treść instrukcji (Markdown)</Label>
                  <Textarea
                    id="edit-instruction-content"
                    value={editingInstruction.content}
                    onChange={(e) => setEditingInstruction({
                      ...editingInstruction,
                      content: e.target.value
                    })}
                    placeholder="Wpisz instrukcję w formacie Markdown..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-instruction-active"
                    checked={editingInstruction.is_active}
                    onChange={(e) => setEditingInstruction({
                      ...editingInstruction,
                      is_active: e.target.checked
                    })}
                    className="rounded"
                  />
                  <Label htmlFor="edit-instruction-active">Instrukcja aktywna</Label>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditInstructionDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handleUpdateInstruction}>
                Zapisz zmiany
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </CardContent>
    </Card>

    {/* Modal dla Instrukcji */}
    <InstructionModal
      instruction={selectedInstruction}
      isOpen={isInstructionModalOpen}
      onClose={() => setIsInstructionModalOpen(false)}
    />
    </>
  );
}
